import { FC, useState, useEffect, SetStateAction, Dispatch } from 'react';
import { Modal, Text, TabGroup, Tab, StatusIconProtoProps, Icon } from '@nike/eds';
import './pass.css';
import { Guest } from '../guest/guest-pass';
import ts from './i18n';
import { useI18nContext } from '../../../context/language/use-i18n-context';
import { usePassPolicies } from '../../../context/PassPolicy';
import { VIPPass } from '../vip/vip-pass';
import { VisitorPass } from '../../../models/IVisitorPass';
import { Vendor } from '../vendor/vendor-pass';
import { CompPass } from '../comp/comp-pass';
import { useUserContext } from '../../../context/user';

export interface PassProps {
  modalOpen: boolean;
  onDismiss: () => void;
  passToEdit: VisitorPass | null;
  setPassSnackbar: Dispatch<
    SetStateAction<{
      showSnack: boolean;
      message: string;
      snackStatus: StatusIconProtoProps['status'];
    }>
  >;
}

const Pass: FC<PassProps> = ({ modalOpen, onDismiss, setPassSnackbar, passToEdit }) => {
  const { i18nString } = useI18nContext();
  const passPoliciesContext = usePassPolicies();
  const userContext = useUserContext();
  const [passState, setPassState] = useState({
    showGuest: false,
    showComp: false,
    showVen: false,
    showVip: false,
    activeId: '',
  });
  const [isBulkUpload, setIsBulkUpload] = useState(false);

  useEffect(() => {
    if (passToEdit && modalOpen) {
      setPassState({ ...passState, activeId: passToEdit?.passTypeToken.split('_')[2] || '' });
    } else if (passToEdit === null) {
      setPassState(prevState => {
        const newPassState = { ...prevState };
        passPoliciesContext.passPolicies.forEach(policy => {
          if (policy.passTypeToken === 'PASS_TYPE_GUEST') {
            newPassState.showGuest = true;
            setPassState({ ...passState, activeId: 'GUEST' });
          }
          if (policy.passTypeToken === 'PASS_TYPE_VIP') {
            if (
              policy?.passesAllowed === 0 ||
              policy?.passesAllowed - (policy?.passesUsed || 0) > 0
            ) {
              newPassState.showVip = true;
            }
            if (passState.activeId === '') {
              setPassState({ ...passState, activeId: 'VIP' });
            }
          }
          if (policy.passTypeToken === 'PASS_TYPE_VENDOR') {
            newPassState.showVen = true;
            if (passState.activeId === '') {
              setPassState({ ...passState, activeId: 'VENDOR' });
            }
          }
          if (policy.passTypeToken === 'PASS_TYPE_COMP') {
            newPassState.showComp = true;
            if (passState.activeId === '') {
              setPassState({ ...passState, activeId: 'COMP' });
            }
          }
        });
        return newPassState;
      });
    }
  }, [passPoliciesContext.passPolicies, modalOpen]);

  const setActiveId = (id: string) => {
    setPassState({ ...passState, activeId: id });
  };

  const renderTabContent = () => {
    switch (passState.activeId) {
      case 'GUEST':
        return (
          <Guest
            onDismiss={onDismiss}
            setSnackBar={setPassSnackbar}
            guestPassToEdit={passToEdit}
            isModleOpen={modalOpen}
          />
        );
      case 'VENDOR':
        return (
          <Vendor
            onDismiss={onDismiss}
            setSnackBar={setPassSnackbar}
            vendorPassToEdit={passToEdit}
            isBulkUpload={isBulkUpload}
            handleBulkUpload={() => setIsBulkUpload(false)}
          />
        );
      case 'COMP':
        return (
          <CompPass
            onDismiss={onDismiss}
            setSnackBar={setPassSnackbar}
            editCompPassModal={passToEdit}
          />
        );
      case 'VIP':
        return (
          <VIPPass onDismiss={onDismiss} setSnackBar={setPassSnackbar} vipPassToEdit={passToEdit} />
        );
      default:
        return null;
    }
  };
  const headerTitle = (): string => {
    if ((passToEdit?.passId ?? 0) > 0) {
      const passType = passToEdit?.passTypeToken.split('_')[2] || '';
      return i18nString(ts.EDIT_PASS_MODAL_TITLE).replace(/\{{\w+\}}/g, passType);
    } else {
      return i18nString(ts.PASS_MODAL_TITLE);
    }
  };

  const Header = () => {
    return (
      <section className="wrapper eds-flex eds-flex--direction-column eds-flex--justify-content-center eds-flex--align-items-center">
        <div className="eds-flex eds-flex--direction-row eds-flex--justify-content-space-between modal_header">
          <span>&nbsp;</span>
          <Text
            className="txt_uppercase modal__heading"
            as={'h1'}
            data-testid="modal__pass__header"
          >
            {headerTitle()}
          </Text>

          <div className="button__upload" data-testid="pass__modal__header__upload">
            {userContext.passplayUser?.canDoBulkUpload && passState.activeId === 'VENDOR' && (
              <div className="eds-flex-inline eds-flex--align-items-center eds-flex--direction-column eds-flex-end">
                <Icon
                  onClick={() => setIsBulkUpload(true)}
                  className="button--action__icon"
                  name="PlusCircle"
                />
                <span className="txt_uppercase text_bulkupload">
                  {i18nString(ts.BULK_UPLOAD_ACTION_TEXT)}
                </span>
              </div>
            )}
          </div>
        </div>
        {passToEdit === null && (
          <div className="eds-flex eds-flex--justify-content-center eds-spacing--mt-40">
            <TabGroup
              name="Pass Types"
              data-testid="toggle-bar"
              activeId={passState.activeId}
              onChange={e => setActiveId(e.target.id)}
            >
              {passState.showGuest && (
                <Tab id="GUEST" value="GUEST">
                  {i18nString(ts.PASS_MODAL_GUEST_TAB)}
                </Tab>
              )}
              {passState.showVip && (
                <Tab id="VIP" value="VIP">
                  {i18nString(ts.PASS_MODAL_VIP_TAB)}
                </Tab>
              )}
              {passState.showVen && (
                <Tab id="VENDOR" value="VENDOR">
                  {i18nString(ts.PASS_MODAL_VENDOR_TAB)}
                </Tab>
              )}
              {passState.showComp && (
                <Tab id="COMP" value="COMP">
                  {i18nString(ts.PASS_MODAL_COMP_TAB)}
                </Tab>
              )}
            </TabGroup>
          </div>
        )}
      </section>
    );
  };

  return (
    <>
      <Modal
        isOpen={modalOpen}
        onDismiss={onDismiss}
        headerSlot={<Header />}
        data-testid="modal__pass"
        className="pass__modal-container"
      >
        <section>{renderTabContent()}</section>
      </Modal>
    </>
  );
};

export default Pass;
